import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  QrCodeIcon,
  MagnifyingGlassIcon,
  CubeIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { usePageSize } from '../../hooks/usePageSize';
import { PageSizeSelect } from '../../components/ui/PageSizeSelect';
import { api } from '../../services/api';
import type { StockState, StockProduct } from '../../types/inventory';
import { STATE_LABELS, STATE_COLORS, ALL_STATES, VALID_NEXT_STATES } from '../../types/inventory';
import { useToastStore } from '../../store/toastStore';
import { ReceiveStockModal } from '../../components/admin/ReceiveStockModal';

// Dropdown of lifecycle states for changing one item's (or a selection's) state.
// Shows every state but only enables those that are a legal next transition for
// ALL the given current states; the backend enforces the same rules.
function StateMenu({
  currentStates,
  onPick,
  trigger,
  align = 'left',
  disabled,
}: {
  currentStates: StockState[];
  onPick: (s: StockState) => void;
  trigger: React.ReactNode;
  align?: 'left' | 'right';
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const enabled = new Set<StockState>(
    ALL_STATES.filter(
      (s) => currentStates.length > 0 && currentStates.every((cur) => (VALID_NEXT_STATES[cur] ?? []).includes(s)),
    ),
  );
  return (
    <div className="relative inline-block text-left">
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} className="disabled:opacity-50">
        {trigger}
      </button>
      {open && (
        <>
          <button type="button" aria-hidden tabIndex={-1} onClick={() => setOpen(false)} className="fixed inset-0 z-10 cursor-default" />
          <div className={`absolute z-20 mt-1 ${align === 'right' ? 'right-0' : 'left-0'} w-44 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1`}>
            <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-slate-500">Change state to</p>
            {ALL_STATES.map((s) => {
              const isEnabled = enabled.has(s);
              return (
                <button
                  key={s}
                  type="button"
                  disabled={!isEnabled}
                  onClick={() => { setOpen(false); onPick(s); }}
                  className={`w-full text-left px-3 py-1.5 flex items-center ${isEnabled ? 'hover:bg-slate-700' : 'opacity-30 cursor-not-allowed'}`}
                  title={isEnabled ? undefined : 'Not a valid transition from the current state'}
                >
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATE_COLORS[s]}`}>
                    {STATE_LABELS[s]}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  state,
  activeState,
  onClick,
}: {
  label: string;
  value: number;
  state?: StockState;
  activeState?: StockState | null;
  onClick?: () => void;
}) {
  const isActive = state != null && activeState === state;
  return (
    <button
      onClick={onClick}
      className={`bg-slate-800/50 border rounded-xl p-4 text-left transition-colors ${
        isActive ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-slate-700 hover:border-slate-600'
      }`}
    >
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className={`text-sm mt-1 ${isActive ? 'text-cyan-400' : 'text-slate-400'}`}>{label}</div>
    </button>
  );
}

export function AdminInventoryPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('inventory');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<StockState | null>(null);
  const [productFilter, setProductFilter] = useState<string>('');
  const [missingMac, setMissingMac] = useState(false);
  const [receiveProduct, setReceiveProduct] = useState<StockProduct | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: stats } = useQuery({
    queryKey: ['admin', 'inventory', 'stats'],
    queryFn: () => api.adminGetInventoryStockStats(),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['admin', 'inventory', 'products', false],
    queryFn: () => api.adminListStockProducts(false),
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'inventory', 'items', { page, pageSize, search, state: stateFilter, product_id: productFilter, missing_mac: missingMac }],
    queryFn: () => api.adminListStockItems({
      page,
      limit: pageSize,
      state: stateFilter ?? undefined,
      product_id: productFilter || undefined,
      search: search || undefined,
      missing_mac: missingMac || undefined,
    }),
  });

  const items = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? 1;

  // Selection is scoped to the current page/filter view, so reset it whenever
  // that view changes — keeps the bulk bar's state-intersection well-defined.
  useEffect(() => { setSelected(new Set()); }, [page, search, stateFilter, productFilter, missingMac]);

  // Reset to the first page whenever the page size, search, or filters change.
  useEffect(() => { setPage(1); }, [pageSize, search, stateFilter, productFilter, missingMac]);

  const selectedStates = items.filter(i => selected.has(i.id)).map(i => i.state);
  const allOnPageSelected = items.length > 0 && items.every(i => selected.has(i.id));

  function toggleAll() {
    setSelected(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach(i => next.delete(i.id));
      else items.forEach(i => next.add(i.id));
      return next;
    });
  }
  function toggleOne(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const rowMutation = useMutation({
    mutationFn: ({ id, to }: { id: string; to: StockState }) => api.adminTransitionStockState(id, to),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'inventory'] }),
    onError: (err: any) => {
      useToastStore.getState().addToast({
        type: 'error',
        title: 'State change failed',
        message: err?.response?.data?.error ?? 'Could not change state',
      });
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (to: StockState) => api.adminBulkTransitionStockItems([...selected], to),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin', 'inventory'] });
      setSelected(new Set());
      useToastStore.getState().addToast({
        type: res.failed > 0 ? 'warning' : 'success',
        title: 'State updated',
        message: res.failed > 0
          ? `${res.succeeded} updated · ${res.failed} skipped (invalid transition)`
          : `${res.succeeded} item${res.succeeded === 1 ? '' : 's'} updated`,
      });
    },
  });

  function handleStateFilter(s: StockState) {
    setStateFilter(prev => prev === s ? null : s);
    setPage(1);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Inventory</h1>
          <p className="text-slate-400 mt-1">Stock items across all hardware types</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/build-station"
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white border border-slate-700 hover:border-slate-600 rounded-lg transition-colors"
          >
            <WrenchScrewdriverIcon className="w-4 h-4" />
            Build Station
          </Link>
          <Link
            to="/admin/inventory/products"
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white border border-slate-700 hover:border-slate-600 rounded-lg transition-colors"
          >
            <Cog6ToothIcon className="w-4 h-4" />
            Products
          </Link>
          {products.length > 0 && (
            <div className="relative">
              {products.length === 1 ? (
                <button
                  onClick={() => setReceiveProduct(products[0])}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <QrCodeIcon className="w-4 h-4" />
                  Receive Stock
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-400 mr-1">Receive:</span>
                  {products.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setReceiveProduct(p)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-medium rounded-lg transition-colors"
                    >
                      <QrCodeIcon className="w-3.5 h-3.5" />
                      {p.sku}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-3">
          <StatCard
            label="Total"
            value={stats.total}
            activeState={stateFilter}
            onClick={() => { setStateFilter(null); setPage(1); }}
          />
          {ALL_STATES.map(s => (
            <StatCard
              key={s}
              label={STATE_LABELS[s]}
              value={stats.byState[s] ?? 0}
              state={s}
              activeState={stateFilter}
              onClick={() => handleStateFilter(s)}
            />
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search serial, batch…"
            className="w-full pl-9 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none placeholder-slate-500"
          />
        </div>
        <select
          value={productFilter}
          onChange={e => { setProductFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All products</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button
          onClick={() => { setMissingMac(m => !m); setPage(1); }}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
            missingMac
              ? 'border-amber-500/50 bg-amber-500/10 text-amber-400'
              : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
          }`}
          title="Show only items with no WiFi MAC captured (can't enter build yet)"
        >
          <WrenchScrewdriverIcon className="w-4 h-4" />
          Needs MAC
        </button>
        <button
          onClick={() => refetch()}
          className="p-2 text-slate-400 hover:text-white border border-slate-700 rounded-lg transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-slate-800 border border-cyan-500/30 rounded-lg px-4 py-2.5">
          <span className="text-sm text-white font-medium">{selected.size} selected</span>
          <StateMenu
            currentStates={selectedStates}
            onPick={(s) => bulkMutation.mutate(s)}
            disabled={bulkMutation.isPending}
            trigger={
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg transition-colors">
                {bulkMutation.isPending ? 'Updating…' : 'Change state'}
                <ChevronDownIcon className="w-4 h-4" />
              </span>
            }
          />
          <button
            onClick={() => setSelected(new Set())}
            className="text-sm text-slate-400 hover:text-white ml-auto transition-colors"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
          <ArrowPathIcon className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
          <CubeIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 mb-4">
            {stateFilter || search || productFilter || missingMac
              ? 'No items match the current filters.'
              : 'No stock items yet. Use Receive Stock to scan in your first batch.'}
          </p>
          {!stateFilter && !search && !productFilter && !missingMac && products.length > 0 && (
            <button
              onClick={() => setReceiveProduct(products[0])}
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg transition-colors"
            >
              <QrCodeIcon className="w-4 h-4" />
              Receive First Item
            </button>
          )}
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                    title="Select all on this page"
                  />
                </th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Serial</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Product</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Batch</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">State</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">IMEI 1</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">MAC</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Mfg Date</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Received</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {items.map(item => (
                <tr key={item.id} className={`transition-colors ${selected.has(item.id) ? 'bg-cyan-500/5' : 'hover:bg-slate-700/20'}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={() => toggleOne(item.id)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/inventory/${item.id}`}
                      className="font-mono text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                      {item.serialNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs">{item.product?.sku ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{item.batchTag ?? '—'}</td>
                  <td className="px-4 py-3">
                    {VALID_NEXT_STATES[item.state].length > 0 ? (
                      <StateMenu
                        currentStates={[item.state]}
                        onPick={(s) => rowMutation.mutate({ id: item.id, to: s })}
                        trigger={
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${STATE_COLORS[item.state]}`}>
                            {STATE_LABELS[item.state]}
                            <ChevronDownIcon className="w-3 h-3 opacity-70" />
                          </span>
                        }
                      />
                    ) : (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATE_COLORS[item.state]}`}>
                        {STATE_LABELS[item.state]}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">
                    {(item.attributes as any)?.imei1 ?? '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">
                    {item.wifiMacAddress ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {(item.attributes as any)?.manufacture_date ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(item.receivedAt).toLocaleDateString('en-NZ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="px-4 py-3 border-t border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <PageSizeSelect value={pageSize} onChange={setPageSize} />
              <span className="text-xs text-slate-500">
                {total} items · page {page} of {totalPages}
              </span>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page <= 1}
                  className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages}
                  className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Receive Modal */}
      {receiveProduct && (
        <ReceiveStockModal
          product={receiveProduct}
          onClose={(received) => {
            setReceiveProduct(null);
            if (received > 0) {
              qc.invalidateQueries({ queryKey: ['admin', 'inventory'] });
            }
          }}
        />
      )}
    </div>
  );
}
